<template>
    <div class="result-accordion" :class="['is-' + variant, open ? 'is-open' : '']">
        <button type="button" class="result-accordion__head" :aria-expanded="open ? 'true' : 'false'" @click="open = !open">
            <span class="result-accordion__icon"><i class="fal fa-fw" :class="icon"></i></span>
            <span class="result-accordion__title">{{ title }}</span>
            <span class="badge ml-3" :class="'badge-' + variant">{{ items.length }}</span>
            <span class="result-accordion__chev"><i class="fal fa-fw fa-chevron-right"></i></span>
        </button>

        <ul class="result-accordion__list" ref="list" v-if="open" :style="{'max-height': listMaxHeight}">
            <li v-for="item in items">
                <div class="result-accordion__item">
                    <b class="d-block"><i class="fal fa-fw fa-user mr-2 align-middle"></i>{{ item.name }}</b>
                    <span v-if="item.message">{{ item.message }}</span>
                </div>
                <a v-if="editUrl" :href="editUrl.replace('__id__', item.id)" class="btn btn-sm btn-outline-primary border-0 ml-2" :title="editTitle" target="_blank">
                    <i class="far fa-fw fa-pencil"></i>
                </a>
            </li>
        </ul>
    </div>
</template>

<script>
    // the list scrolls when it has more items than this
    const ITEMS_LIMIT = 5;

    export default {
        props: {
            'title': { type: String },
            'items': { type: Array },
            'variant': { type: String, default: 'warning' }, // success | warning | danger
            'icon': { type: String, default: 'fa-exclamation-triangle' },
            'collapsed': { type: Boolean, default: false }, // collapsed by default, otherwise open
            'editUrl': { type: String, default: null }, // item edit link, "__id__" is replaced by the item id
            'editTitle': { type: String }
        },

        data() {
            return {
                open: !this.collapsed,
                listMaxHeight: null
            };
        },

        watch: {
            // immediate - the accordion can be open right after it is rendered
            open: {
                immediate: true,
                handler(isOpen) {
                    if (isOpen && this.items.length > ITEMS_LIMIT) {
                        this.$nextTick(() => this.limitListHeight());
                    }
                }
            }
        },

        methods: {
            // items differ in height (name only, or name + 1-3 lines of message),
            // so the list is cut right below the last item which still fits the limit
            limitListHeight() {
                const list = this.$refs.list;
                const lastVisible = list.children[ITEMS_LIMIT - 1];

                this.listMaxHeight = Math.ceil(lastVisible.getBoundingClientRect().bottom - list.getBoundingClientRect().top) + 'px';
            }
        }
    }
</script>

<style lang="scss">
    // accordion styles copied from export-exclusions.scss (send to accounting modal)
    .result-accordion {
        border: 1px solid #e7edf2;
        border-radius: 4px;
        overflow: hidden;
        background: #fff;

        & + & { margin-top: 8px; }

        &.is-open { border-color: #d6dee3; }

        &__head {
            display: flex;
            align-items: center;
            width: 100%;
            padding: 11px 14px 11px 0;
            background: none;
            border: 0;
            border-left: 3px solid transparent;
            font-family: inherit;
            text-align: left;
            cursor: pointer;

            &:hover { background: #F5F8FF; }
            &:focus { outline: none; }
            &:focus-visible { outline: 2px solid #4273FA; outline-offset: -2px; }
        }

        &__icon {
            flex: none;
            width: 28px;
            height: 28px;
            margin-left: 11px;
            margin-right: 12px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 13px;
        }

        &__title {
            font-size: 14px;
            font-weight: 600;
            color: #43484f;
        }

        &__chev {
            flex: none;
            margin-left: auto;
            color: #b9c8de;
            transition: transform .15s ease;
        }

        &.is-open .result-accordion__chev {
            transform: rotate(90deg);
        }

        // severity colouring
        &.is-danger {
            .result-accordion__head { border-left-color: #FE3F61; }
            .result-accordion__icon { background: #fff1f3; color: #FE3F61; }
        }

        &.is-warning {
            .result-accordion__head { border-left-color: #F5BE30; }
            .result-accordion__icon { background: #fff9e9; color: #a8791a; }
        }

        &.is-success {
            .result-accordion__head { border-left-color: #60CF7D; }
            .result-accordion__icon { background: #eefaf1; color: #3aa85b; }
        }

        &__item {
            flex: 1 1 auto;
            min-width: 0;
        }

        &__list {
            list-style: none;
            margin: 0;
            padding: 0;
            overflow-y: auto;
            border-top: 1px solid #e7edf2;

            li {
                display: flex;
                align-items: center;
                margin: 0;
                padding: 6px 14px;
                border-bottom: 1px solid #e7edf2;

                &:last-child { border-bottom: 0; }
                &:hover { background: #F5F8FF; }
            }
        }
    }
</style>



// WEBPACK FOOTER //
// resources/assets/admin/js/components/BatchActionResultAccordion.vue